import { Phone, Waves, Droplets, ScanSearch, Siren, Clock, HardHat } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { PageHero } from "@/components/site/PageHero";
import { MaskLines, Reveal } from "@/components/site/Reveal";
import { CtaBand } from "@/components/site/CtaBand";
import { SITE } from "@/lib/site";

const SERVICES_247 = [
  { I: Droplets, title: "Blocked drains", text: "Sinks, showers, toilets, stormwater and sewer lines cleared fast — inside or out, residential or commercial." },
  { I: Waves, title: "Jet blasting", text: "High-pressure water jetting cuts through tree roots, grease and built-up debris to restore full flow without digging." },
  { I: ScanSearch, title: "CCTV camera inspections", text: "We send a camera down the line to find the exact cause and location of the problem, so the fix is right first time." },
  { I: Siren, title: "Plumbing emergencies", text: "Burst pipes, major leaks, overflowing toilets and sewer backups — call any time and we'll talk you through it." },
];

const STEPS = [
  ["Call our 24/7 line", `Ring ${SITE.phone} any time, day or night. Tell us what's happening and we'll let you know when we can be there.`],
  ["Find the cause", "We assess on site and, where needed, run a CCTV camera inspection to see exactly what's going on."],
  ["Clear it & fix it", "Jet blasting, repairs or replacement — then we clean up and explain what we found."],
];

const Hero247 = () => (
  <div className="relative">
    <PageHero testid="emergency-hero" eyebrow="24/7 service division" lines={["Blocked drain?", "Day or night,", "we're on it."]}
      intro="Our 24-hour division handles blocked drains, jet blasting, CCTV camera inspections and plumbing emergencies — around the clock, 7 days a week." />
    <p aria-hidden="true" className="hidden xl:block pointer-events-none absolute right-[4vw] bottom-16 font-wide font-black text-[15rem] leading-none tracking-tighter text-transparent [-webkit-text-stroke:2px_rgba(91,143,230,0.35)] select-none">24/7</p>
  </div>
);

const CallStrip = () => (
  <section className="bg-cobalt text-white">
    <div className="container-x py-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
      <div className="flex items-center gap-4">
        <span className="relative flex h-3 w-3"><span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-70 animate-ping" /><span className="relative inline-flex h-3 w-3 rounded-full bg-white" /></span>
        <p className="font-wide font-extrabold text-xl sm:text-2xl">24/7 emergency line — open now</p>
      </div>
      <a href={SITE.tel} data-testid="emergency-call-button" className="group inline-flex items-center justify-center gap-3 bg-white text-ink font-bold h-16 px-8 rounded-full hover:bg-ink hover:text-white transition-colors">
        <Phone className="h-5 w-5" /> Call {SITE.phone}
      </a>
    </div>
  </section>
);

const ServiceCards = () => (
  <section data-testid="emergency-services" className="bg-porcelain py-24 sm:py-32">
    <div className="container-x">
      <p className="eyebrow text-cobalt mb-5">What our 24/7 team does</p>
      <h2 className="font-wide font-black uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl"><MaskLines lines={["Drains & emergencies,", "sorted any hour."]} /></h2>
      <div className="mt-14 grid gap-4 sm:grid-cols-2">
        {SERVICES_247.map(({ I, title, text }, i) => (
          <Reveal key={title} delay={i * 0.06} className="group bg-white rounded-2xl shadow-[0_8px_30px_-12px_rgba(11,25,44,0.15)] p-8 transition-transform duration-500 hover:-translate-y-1">
            <div data-testid={`emergency-service-${i}`}>
              <I className="h-8 w-8 text-cobalt" />
              <h3 className="mt-6 font-wide font-black uppercase text-2xl tracking-tight">{title}</h3>
              <p className="mt-3 text-slate-600 leading-relaxed">{text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const HoursBlock = () => (
  <section data-testid="emergency-hours" className="bg-ink text-white py-24 sm:py-32 relative overflow-hidden">
    <div className="absolute inset-0 blueprint" />
    <div className="container-x relative grid gap-12 lg:grid-cols-12 items-center">
      <div className="lg:col-span-5">
        <p className="eyebrow text-brass mb-5">Our hours</p>
        <h2 className="font-wide font-black uppercase text-4xl sm:text-5xl tracking-tight"><MaskLines lines={["Two teams.", "One number."]} /></h2>
        <p className="mt-6 text-slate-300 leading-relaxed max-w-md">Our construction and on-site crews work standard trade hours. Our 24/7 division never clocks off for drains and emergencies.</p>
      </div>
      <div className="lg:col-span-7 grid gap-4 sm:grid-cols-2">
        <Reveal className="rounded-2xl bg-white/5 border border-white/10 p-8">
          <HardHat className="h-7 w-7 text-brass" />
          <p className="eyebrow text-slate-400 mt-6">On-site & construction</p>
          <p data-testid="hours-onsite" className="font-wide font-black text-3xl mt-2">{SITE.hoursSite}</p>
          <p className="text-slate-300 mt-1">{SITE.hoursSiteTime}</p>
        </Reveal>
        <Reveal delay={0.08} className="rounded-2xl bg-brass text-ink p-8">
          <Clock className="h-7 w-7" />
          <p className="eyebrow mt-6 opacity-80">24/7 division</p>
          <p data-testid="hours-emergency" className="font-wide font-black text-3xl mt-2">24 hours · 7 days</p>
          <p className="mt-1">Blocked drains, jet blasting, CCTV & emergencies</p>
        </Reveal>
      </div>
    </div>
  </section>
);

const StepsSection = () => (
  <section className="bg-bone py-24 sm:py-32">
    <div className="container-x">
      <p className="eyebrow text-cobalt mb-5">How it works</p>
      <h2 className="font-wide font-black uppercase text-4xl sm:text-5xl tracking-tight"><MaskLines lines={["From call-out", "to clear."]} /></h2>
      <ol className="mt-14 grid gap-4 md:grid-cols-3">
        {STEPS.map(([t, d], i) => (
          <Reveal as="li" key={t} delay={i * 0.06} className="bg-white rounded-2xl shadow-[0_8px_30px_-12px_rgba(11,25,44,0.15)] p-8">
            <span data-testid={`emergency-step-${i}`} className="font-mono text-cobalt font-bold text-lg">0{i + 1}</span>
            <p className="mt-4 font-wide font-extrabold text-xl">{t}</p>
            <p className="mt-2 text-slate-600 leading-relaxed">{d}</p>
          </Reveal>
        ))}
      </ol>
    </div>
  </section>
);

export default function TwentyFourSeven() {
  return (
    <>
      <Seo title="24/7 Emergency Plumber Sydney | Austyle Plumbing"
        description="24-hour blocked drains, jet blasting, CCTV drain camera inspections and plumbing emergencies across Sydney. Call Austyle Plumbing any time on 0452 538 075." />
      <Hero247 />
      <CallStrip />
      <ServiceCards />
      <HoursBlock />
      <StepsSection />
      <CtaBand title={["Drain emergency?", "Call us now."]} />
    </>
  );
}
